import React, { useContext } from "react";
import { Container, Card, CardBody, Row, Col } from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext";
import { useNavigate } from "react-router-dom";

const CallsOverview = ({ userData }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();

  const monitoringCards = [
    {
      title: "Global Calls",
      icon: "fas fa-phone-alt",
      route: "/admin/calls-overview",
      accent: isDark ? "#38bdf8" : "#0ea5e9",
    },
    {
      title: "Global Registrations",
      icon: "fas fa-user-check",
      route: "/admin/registrations-overview",
      accent: isDark ? "#2dd4bf" : "#14b8a6",
    },
    {
      title: "System Status",
      icon: "fas fa-server",
      route: "/admin/hucs/system-status",
      accent: isDark ? "#a78bfa" : "#8b5cf6",
    },
    {
      title: "SIPS Status",
      icon: "fas fa-network-wired",
      route: "/admin/hucs/sips-status",
      accent: isDark ? "#fb923c" : "#f97316",
    },
  ];

  const advancedCards = [
    {
      title: "Access Controls",
      icon: "fas fa-shield-alt",
      route: "/admin/hucs/access-controls",
      accent: isDark ? "#f472b6" : "#ec4899",
    },
    {
      title: "Databases",
      icon: "fas fa-database",
      route: "/admin/hucs/databases",
      accent: isDark ? "#38bdf8" : "#0ea5e9",
    },
    {
      title: "Default Settings",
      icon: "fas fa-cog",
      route: "/admin/hucs/default-settings",
      accent: isDark ? "#b3b3b3" : "#5a5a5a",
    },
    {
      title: "Domains",
      icon: "fas fa-globe",
      route: "/admin/hucs/domains",
      accent: isDark ? "#a78bfa" : "#8b5cf6",
    },
    {
      title: "Email Templates",
      icon: "fas fa-envelope",
      route: "/admin/hucs/email-templates",
      accent: isDark ? "#fb923c" : "#f97316",
    },
    {
      title: "Modules",
      icon: "fas fa-puzzle-piece",
      route: "/admin/hucs/modules",
      accent: isDark ? "#f472b6" : "#ec4899",
    },
    {
      title: "Num Translations",
      icon: "fas fa-exchange-alt",
      route: "/admin/hucs/number-translations",
      accent: isDark ? "#38bdf8" : "#0ea5e9",
    },
    {
      title: "SIP Profiles",
      icon: "fas fa-address-card",
      route: "/admin/hucs/sip-profiles",
      accent: isDark ? "#2dd4bf" : "#14b8a6",
    },
    {
      title: "Variables",
      icon: "fas fa-code",
      route: "/admin/hucs/variables",
      accent: isDark ? "#a78bfa" : "#8b5cf6",
    },
  ];

  return (
    <Container
      fluid
      className="mt--5 px-4"
      style={{
        minHeight: "85vh",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center mb-4"
        style={{
          borderBottom: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
          paddingBottom: "0.75rem",
        }}
      >
        <div>
          <h2
            className="fw-bold mb-1"
            style={{
              color: isDark ? "#f9fafb" : "#111827",
            }}
          >
            Calls Dashboard
          </h2>
          <div
            style={{
              height: "3px",
              width: "100%",
              borderRadius: "3px",
              background: "linear-gradient(90deg, #0ea5e9, #14b8a6)",
            }}
          />
        </div>
      </div>

      {/* Monitoring Section */}
      <div className="mb-4">
        <div
          className="d-flex justify-content-between align-items-center mb-4"
          style={{
            borderBottom: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
            paddingBottom: "0.75rem",
          }}
        >
          <div>
            <h4
              className="fw-bold mb-1"
              style={{
                color: isDark ? "#f9fafb" : "#111827",
                fontSize: "1.25rem"
              }}
            >
              Monitoring
            </h4>
            <div
              style={{
                height: "3px",
                width: "100%",
                borderRadius: "3px",
                background: "linear-gradient(90deg, #0ea5e9, #14b8a6)",
              }}
            />
          </div>
        </div>
        <Row className="gy-3 gx-3">
          {monitoringCards.map((card, idx) => (
            <Col key={idx} xs="12" sm="6" md="4" lg="3" xl="3">
              <Card
                className="border-0"
                style={{
                  height: "160px",
                  borderRadius: "0.75rem",
                  backgroundColor: isDark ? "#020617" : "#ffffff",
                  color: isDark ? "#e5e7eb" : "#111827",
                  boxShadow: isDark
                    ? "0 8px 24px rgba(0,0,0,0.6)"
                    : "0 8px 24px rgba(0,0,0,0.1)",
                  transition: "transform 0.15s ease, box-shadow 0.2s ease",
                  cursor: "pointer",
                }}
                onClick={() => navigate(card.route)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = isDark
                    ? "0 12px 30px rgba(0,0,0,0.75)"
                    : "0 12px 30px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = isDark
                    ? "0 8px 24px rgba(0,0,0,0.6)"
                    : "0 8px 24px rgba(0,0,0,0.1)";
                }}
              >
                <CardBody className="d-flex flex-column justify-content-center align-items-center text-center py-3">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "2.5rem",
                      color: card.accent,
                    }}
                  >
                    <i className={card.icon} />
                  </div>
                  <h5
                    className="fw-semibold mb-0"
                    style={{
                      color: isDark ? "#f9fafb" : "#111827",
                      fontSize: "0.95rem",
                      lineHeight: "1.3",
                    }}
                  >
                    {card.title}
                  </h5>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Advanced Section */}
      <div>
        <div
          className="d-flex justify-content-between align-items-center mb-4"
          style={{
            borderBottom: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
            paddingBottom: "0.75rem",
          }}
        >
          <div>
            <h4
              className="fw-bold mb-1"
              style={{
                color: isDark ? "#f9fafb" : "#111827",
                fontSize: "1.25rem"
              }}
            >
              Advanced
            </h4>
            <div
              style={{
                height: "3px",
                width: "100%",
                borderRadius: "3px",
                background: "linear-gradient(90deg, #0ea5e9, #14b8a6)",
              }}
            />
          </div>
        </div>
        <Row className="gy-3 gx-3">
          {advancedCards.map((card, idx) => (
            <Col key={idx} xs="12" sm="6" md="4" lg="4" xl="4">
              <Card
                className="border-0"
                style={{
                  height: "160px",
                  borderRadius: "0.75rem",
                  backgroundColor: isDark ? "#020617" : "#ffffff",
                  color: isDark ? "#e5e7eb" : "#111827",
                  boxShadow: isDark
                    ? "0 8px 24px rgba(0,0,0,0.6)"
                    : "0 8px 24px rgba(0,0,0,0.1)",
                  transition: "transform 0.15s ease, box-shadow 0.2s ease",
                  cursor: "pointer",
                }}
                onClick={() => navigate(card.route)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = isDark
                    ? "0 12px 30px rgba(0,0,0,0.75)"
                    : "0 12px 30px rgba(0,0,0,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = isDark
                    ? "0 8px 24px rgba(0,0,0,0.6)"
                    : "0 8px 24px rgba(0,0,0,0.1)";
                }}
              >
                <CardBody className="d-flex flex-column justify-content-center align-items-center text-center py-3">
                  <div
                    className="mb-2"
                    style={{
                      fontSize: "2.5rem",
                      color: card.accent,
                    }}
                  >
                    <i className={card.icon} />
                  </div>
                  <h5
                    className="fw-semibold mb-0"
                    style={{
                      color: isDark ? "#f9fafb" : "#111827",
                      fontSize: "0.95rem",
                      lineHeight: "1.3",
                    }}
                  >
                    {card.title}
                  </h5>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </Container>
  );
};

export default CallsOverview;
